import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Truck, RotateCcw, Zap, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const { isPrelaunch } = useSiteSettings();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="container-tight py-16 font-mono text-zinc-500">LOADING...</div>;
  if (!product) return <div className="container-tight py-16 font-mono text-red-400">PRODUCT NOT FOUND</div>;

  const inStock = product.stock > 0;

  const onAdd = () => {
    if (isPrelaunch) {
      toast.info("Online ordering opens soon", {
        description: "Contact sales@interstate-pc.com for inquiries.",
      });
      return;
    }
    addItem(product, qty);
    toast.success(`Added ${qty} × ${product.name}`);
  };

  return (
    <div className="container-tight py-8" data-testid="product-detail">
      <nav className="font-mono text-xs text-zinc-500 mb-6">
        <Link to="/" className="hover:text-[#FF5C00]">HOME</Link> /{" "}
        <Link to={`/catalog?category=${product.category}`} className="hover:text-[#FF5C00] uppercase">{product.category}</Link> /{" "}
        <span className="text-zinc-300">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
        <div className="tactical-border border border-zinc-800 bg-[#0a0a0c] aspect-square overflow-hidden relative">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          {isPrelaunch && (
            <span className="absolute top-3 right-3 font-mono text-[10px] tracking-widest bg-yellow-500/15 text-yellow-300 border border-yellow-500/40 px-2 py-1 rounded-sm">
              PREVIEW
            </span>
          )}
        </div>

        <div>
          <div className="font-mono text-xs tracking-widest text-[#00F0FF] uppercase mb-2">{product.brand}</div>
          <h1 data-testid="product-name" className="font-display font-black text-3xl md:text-4xl leading-tight mb-4">{product.name}</h1>

          <div className="border-y border-zinc-800 py-6 mb-6">
            <div className="flex items-baseline gap-3">
              <span data-testid="product-price" className="font-mono font-bold text-4xl text-white">{formatPrice(product.price)}</span>
            </div>
            {isPrelaunch && (
              <div className="font-mono text-[10px] text-zinc-500 mt-1 tracking-widest">
                PREVIEW PRICING — NOT FINAL LAUNCH PRICE
              </div>
            )}
            <div className="font-mono text-xs mt-3 flex items-center gap-2 text-yellow-400/90"
                 data-testid="product-availability">
              <span className="w-2 h-2 rounded-full bg-yellow-400/80" />
              {isPrelaunch ? "AVAILABILITY AT LAUNCH" : (inStock ? "IN STOCK" : "OUT OF STOCK")}
            </div>
            <div className="font-mono text-xs text-zinc-500 mt-1">Manufacturer Part #: {product.sku}</div>
          </div>

          <p className="text-zinc-300 leading-relaxed mb-6">{product.description}</p>

          <div className="flex items-center gap-3 mb-6">
            {!isPrelaunch && (
              <div className="flex items-center border border-zinc-800 rounded-sm">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-11 hover:bg-zinc-900 flex items-center justify-center">
                  <Minus className="w-4 h-4" />
                </button>
                <span data-testid="qty-value" className="w-12 text-center font-mono">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(Math.min(product.stock || 99, qty + 1))} className="w-10 h-11 hover:bg-zinc-900 flex items-center justify-center">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
            <Button data-testid="add-to-cart-detail" disabled={isPrelaunch || !inStock} onClick={onAdd}
              className="flex-1 h-11 bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider glow-orange disabled:opacity-40">
              <Zap className="w-4 h-4 mr-2" />{isPrelaunch ? "COMING SOON" : "ADD TO CART"}
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-8">
            {[{icon: Truck, l: "Secure Checkout"}, {icon: ShieldCheck, l: "Stripe · SSL"}, {icon: RotateCcw, l: "Policies TBD"}].map((f, i) => (
              <div key={i} className="border border-zinc-800 p-3 rounded-sm">
                <f.icon className="w-4 h-4 text-[#FF5C00] mb-1" />
                <div className="font-mono text-[10px] tracking-widest text-zinc-400">{f.l}</div>
              </div>
            ))}
          </div>

          <div className="border border-zinc-800 rounded-sm">
            <div className="bg-[#0a0a0c] px-4 py-3 border-b border-zinc-800 font-mono text-xs tracking-widest uppercase text-zinc-300">
              Specifications
            </div>
            <table className="w-full">
              <tbody>
                {Object.entries(product.specs || {}).map(([k, v], i) => (
                  <tr key={k} className={i % 2 ? "bg-[#0a0a0c]" : ""}>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-500 tracking-widest uppercase w-1/3 border-b border-zinc-900">{k}</td>
                    <td className="px-4 py-2.5 font-mono text-sm text-white border-b border-zinc-900">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
